import React from 'react';
import {
  CardFirstRow,
  CardSecondRow,
  CardThirdRow,
  CardFourthRow,
  LineSeparetor,
  CardFifthRow,
  ExecPhoto,
  ExecPhotoWrapper,
  MoreExecContainer,
  StatusIcon,
  OccurrenceCardHeader,
  OccurrenceCardHeaderLeft,
  CqaText,
  SmallActivityCard,
} from '../shared/styled';
import { ReactComponent as PendingAgIcon } from 'assets/icons/pending-ag-icon.svg';
import { ReactComponent as CheckGIcon } from 'assets/icons/check-g.svg';
import { ReactComponent as BullhornIcon } from 'assets/icons/bullhorn_sm.svg';
import { ReactComponent as ExecutorNoPhotoIcon } from 'assets/icons/executor-no-photo-icon.svg';
import { CalendarEvent, EventColorSet } from 'pages/Agenda/utils';
import { useHoverCardPosition } from '../../../hooks/useHoverCardPosition';
import { useEventCardDisplay } from '../../../hooks/useEventCardDisplay';

const OCCURRENCE_BLUE = '#85BFE4';

interface HoverCardProps {
  event: CalendarEvent;
  rect: DOMRect;
  colorSet: EventColorSet;
  isCompletedView: boolean;
}

const ExecutorRows: React.FC<{
  executorCount: number;
  firstExecutor: { nome: string; avatar: string } | undefined;
  displayExecutors: { nome: string; avatar: string }[];
}> = ({ executorCount, firstExecutor, displayExecutors }) => {
  if (executorCount === 1 && firstExecutor) {
    return (
      <>
        {firstExecutor.avatar ? (
          <ExecPhoto src={firstExecutor.avatar} alt={firstExecutor.nome} />
        ) : (
          <ExecPhotoWrapper>
            <ExecutorNoPhotoIcon width={20} height={20} />
          </ExecPhotoWrapper>
        )}
        <span style={{ marginLeft: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {firstExecutor.nome}
        </span>
      </>
    );
  }
  return (
    <MoreExecContainer>
      <div>
        {displayExecutors.map((exec, index) =>
          exec.avatar ? (
            <ExecPhoto
              key={`exec-hover-${exec.nome}-${index}`}
              src={exec.avatar}
              alt={exec.nome}
              style={{ marginLeft: index === 0 ? 0 : -8, zIndex: index, position: 'relative' }}
            />
          ) : (
            <ExecPhotoWrapper
              key={`exec-hover-${exec.nome}-${index}`}
              style={{ marginLeft: index === 0 ? 0 : -8, zIndex: index, position: 'relative' }}
            >
              <ExecutorNoPhotoIcon width={20} height={20} />
            </ExecPhotoWrapper>
          ),
        )}
        <span>{executorCount} executores</span>
      </div>
    </MoreExecContainer>
  );
};

export const HoverCard: React.FC<HoverCardProps> = ({ event, rect: initialRect, colorSet, isCompletedView }) => {
  const isOccurrence = event.eventKind === 'ocorrencia';
  const solucionada = event.realizada || isCompletedView;

  const { positionStyle } = useHoverCardPosition(event.id, initialRect, colorSet, {
    isOccurrence,
    outerBorderColor: OCCURRENCE_BLUE,
  });

  const { atividadesLabel, executorCount, firstExecutor, displayExecutors } = useEventCardDisplay(event);

  if (isOccurrence) {
    const statusDotColor = solucionada ? '#4CAF50' : event.atrasada ? '#E74C3C' : OCCURRENCE_BLUE;
    const statusContent = solucionada ? '✓' : null;

    return (
      <div style={positionStyle}>
        <OccurrenceCardHeader>
          <OccurrenceCardHeaderLeft iconColor={OCCURRENCE_BLUE}>
            <BullhornIcon />
            <CqaText fontColor={OCCURRENCE_BLUE}>{event.numeroChamado}</CqaText>
          </OccurrenceCardHeaderLeft>
          <StatusIcon color={statusDotColor}>{statusContent}</StatusIcon>
        </OccurrenceCardHeader>

        <SmallActivityCard borderLeft={colorSet.borderColor} backgroundColor={colorSet.backgroundColor}>
          <CardFirstRow fontColor={colorSet.fontColor}>
            <span>
              {event.time} • {event.title}
            </span>
          </CardFirstRow>
          <CardSecondRow>{event.aplicacao}</CardSecondRow>
          <CardThirdRow>{[event.equipamento, event.equipamentoTag].filter(Boolean).join(' - ')}</CardThirdRow>
          <CardFourthRow>
            <ExecutorRows
              executorCount={executorCount}
              firstExecutor={firstExecutor}
              displayExecutors={displayExecutors}
            />
          </CardFourthRow>
          <LineSeparetor separetorColor={colorSet.separetorColor} />
          <CardFifthRow style={{ color: colorSet.fontColor }}>Ocorrência ({event.horasPrevistas})</CardFifthRow>
        </SmallActivityCard>
      </div>
    );
  }

  return (
    <div style={positionStyle}>
      <CardFirstRow fontColor={colorSet.fontColor}>
        <span>
          {event.time} • {event.title}
        </span>
        <StatusIcon color="transparent">
          {event.realizada || isCompletedView ? (
            <CheckGIcon width={16} height={16} />
          ) : event.atrasada ? (
            <PendingAgIcon width={12} height={12} />
          ) : null}
        </StatusIcon>
      </CardFirstRow>
      <CardSecondRow>{event.aplicacao}</CardSecondRow>
      <CardThirdRow>{event.equipamento}</CardThirdRow>
      <CardFourthRow>
        <ExecutorRows executorCount={executorCount} firstExecutor={firstExecutor} displayExecutors={displayExecutors} />
      </CardFourthRow>
      <LineSeparetor separetorColor={colorSet.separetorColor} />
      <CardFifthRow>{atividadesLabel}</CardFifthRow>
    </div>
  );
};
